import {
  CLAIM_TYPES,
  CLAIMS_STATUS,
  DATA_ACCESS_ALL_STATUS,
  RELATIONSHIP,
} from "@/constants";
import type {
  CareHistoryClaimsResponseType,
  CareHistoryConfigResponseType,
  CareHistoryGraphResponseType,
  GroupedClaim,
} from "@/model/care-history";
import {
  capMaxDate,
  capMinDate,
  normalizeToISODate,
  parseConfigDate,
} from "@/lib/date-utils";
import type { DataAccessListItem } from "@/model/data-access";
import type { Relation } from "@/model/demographics";
import type {
  CareHistoryFilterStateType,
  CareHistoryGraphItemType,
  ClaimsProcedure,
} from "@/types";
import type { Payload } from "recharts/types/component/DefaultLegendContent";
import type { ClaimStatusLabel } from "@/model/ui-labels";
import { hasDataAccess } from "./data-access-utils";
import { lowerCaseComparison } from "./string-util";

export type CareHistoryServiceDateRange = {
  /**
   * Start date for period (may be undefined if config is missing/unparseable).
   * Used as picker min and as fallback start.
   */
  periodStart?: Date;
  /**
   * End date capped to "today" (always a concrete Date).
   * Used as picker max for end date and as fallback end.
   */
  effectiveMaxEnd: Date;
  /**
   * Normalized "from" used for picker start value (may be undefined).
   */
  uiFrom?: Date;
  /**
   * Normalized "to" used for picker end value (always a concrete Date).
   */
  uiTo: Date;
  /**
   * Normalized start/end used for filtering.
   * Always concrete Dates so the date filter logic stays simple.
   */
  filterStart: Date;
  filterEnd: Date;
};

/**
 * Normalize "Date of Service" range to business rules:
 * - Normalize YYYYMM/plan YYYY-MM-DD strings to Date boundaries
 * - Cap end date to today
 * - Ensure fromDate <= toDate (clamps from down to to if needed)
 *
 * This keeps date behavior consistent between:
 * - the picker UI (care-history.tsx)
 * - the filtering logic (filterByAdvancedSearch)
 */
export function normalizeCareHistoryServiceDateRange(args: {
  claimsFromDate?: string;
  claimsToDate?: string;
  planStartDate?: string;
  planEndDate?: string;
  fromDate?: Date | null;
  toDate?: Date | null;
}): CareHistoryServiceDateRange {
  const periodStart = args.claimsFromDate
    ? parseConfigDate(args.claimsFromDate, "start")
    : args.planStartDate
      ? parseConfigDate(args.planStartDate, "start")
      : undefined;

  const rawPeriodEnd = args.claimsToDate
    ? parseConfigDate(args.claimsToDate, "end")
    : args.planEndDate
      ? parseConfigDate(args.planEndDate, "end")
      : undefined;

  const effectiveMaxEnd = capMaxDate(rawPeriodEnd);

  // Safety/DX: ensure [min..max] is always a valid range for the start-date picker.
  const periodStartForUI = capMinDate(periodStart, effectiveMaxEnd);

  // End: default to effectiveMaxEnd, and cap any user-provided future date.
  const uiToCandidate = args.toDate ?? effectiveMaxEnd;
  const uiTo =
    uiToCandidate > effectiveMaxEnd ? effectiveMaxEnd : uiToCandidate;

  // Additional safety: when user sets an end date earlier than the period start,
  // keep the picker min <= picker max (toDate).
  const periodStartForUIFinal = capMinDate(periodStartForUI, uiTo);

  // From: keep user-provided value if present; otherwise fallback to periodStart.
  // Then ensure from <= to.
  let uiFrom = args.fromDate ?? periodStartForUIFinal;
  if (uiFrom && uiFrom > uiTo) uiFrom = uiTo;

  // Filtering requires concrete Dates; fall back to epoch if periodStart is unavailable.
  const filterStart = uiFrom ?? periodStartForUIFinal ?? new Date(0);
  const filterEnd = uiTo;

  return {
    // picker minStart
    periodStart: periodStartForUIFinal,
    effectiveMaxEnd,
    uiFrom,
    uiTo,
    filterStart,
    filterEnd,
  };
}

export function searchClaimNumbers(
  input: string,
  data: CareHistoryClaimsResponseType[],
): CareHistoryClaimsResponseType[] {
  const trimmed = input.trim();
  return data.filter((item) => item.revClaimId.includes(trimmed));
}

export function searchCareEntity(
  input: string,
  data: CareHistoryClaimsResponseType[],
): string[] {
  const trimmed = input.trim();
  const lowerCaseInput = trimmed.toLowerCase();
  const filteredCareEntity = data
    ?.filter((item) => {
      if (
        item?.claimType === CLAIM_TYPES.MEDICAL.VALUE &&
        item?.prvFullName?.toLowerCase().includes(lowerCaseInput)
      ) {
        return item.prvFullName;
      }
      if (
        item?.claimType === CLAIM_TYPES.PHARMACY.VALUE &&
        item?.prescriberName?.toLowerCase().includes(lowerCaseInput)
      ) {
        return item.prescriberName;
      }
    })
    .map((item) =>
      item.claimType === CLAIM_TYPES.MEDICAL.VALUE
        ? item.prvFullName
        : item.prescriberName,
    );

  return [...new Set(filteredCareEntity)];
}

export function searchProcedure(
  input: string,
  data: CareHistoryClaimsResponseType[],
): ClaimsProcedure[] {
  const trimmed = input.trim();
  const lowerCaseInput = trimmed.toLowerCase();
  const filteredProcedures = data.flatMap((item) =>
    item.procedures.filter(
      (proc) =>
        proc.procedureDesc.toLowerCase().includes(lowerCaseInput) ||
        proc.procedureCode.includes(trimmed),
    ),
  );
  const distinctSearchResultsByProcedureCode = Array.from(
    new Map(filteredProcedures.map((obj) => [obj.procedureCode, obj])).values(),
  );
  return distinctSearchResultsByProcedureCode;
}

export function searchPharmacy(
  input: string,
  data: CareHistoryClaimsResponseType[],
): string[] {
  const trimmed = input.trim();
  const lowerCaseInput = trimmed.toLowerCase();
  const filteredPharmacy = data
    ?.filter((item) => {
      if (
        item?.claimType === CLAIM_TYPES.PHARMACY.VALUE &&
        item?.prvFullName?.toLowerCase().includes(lowerCaseInput)
      ) {
        return item;
      }
    })
    .map((item) => item.prvFullName);
  return [...new Set(filteredPharmacy)];
}

export function searchDrugName(
  input: string,
  data: CareHistoryClaimsResponseType[],
): string[] {
  const trimmed = input.trim();
  const lowerCaseInput = trimmed.toLowerCase();
  const filteredDrugName = data
    ?.filter((item) => {
      if (
        item?.claimType === CLAIM_TYPES.PHARMACY.VALUE &&
        item?.drugName?.toLowerCase().includes(lowerCaseInput)
      ) {
        return item;
      }
    })
    .map((item) => item.drugName);
  return [...new Set(filteredDrugName)];
}

export function filterByClaimNumber(
  data: GroupedClaim[],
  selectedClaimNumber: string | null,
): GroupedClaim[] {
  if (selectedClaimNumber === null) return data;
  return data
    .map((item) => ({
      ...item,
      historyList: (item.historyList || []).filter((history) =>
        selectedClaimNumber.includes(history.revClaimId || ""),
      ),
    }))
    .filter((item) => item.historyList && item.historyList.length > 0);
}

export function filterByAdvancedSearch(
  config: CareHistoryConfigResponseType | undefined,
  groupedClaims: GroupedClaim[],
  filters: CareHistoryFilterStateType,
  claimsFromDate?: string,
  claimsToDate?: string,
): GroupedClaim[] {
  if (!groupedClaims?.length) return [];
  const {
    selectedMembers,
    selectedClaimTimePeriod,
    selectedClaimType,
    selectedCareEntity,
    selectedProcedure,
    selectedPharmacy,
    selectedDrugName,
    sortClaimsBy,
    selectedDateRange,
  } = filters;

  let start: Date | null = null;
  let end: Date | null = null;

  if (selectedClaimTimePeriod === "Date of Service") {
    const normalized = normalizeCareHistoryServiceDateRange({
      claimsFromDate,
      claimsToDate,
      planStartDate: config?.planStartDate,
      planEndDate: config?.planEndDate,
      fromDate: selectedDateRange?.fromDate ?? null,
      toDate: selectedDateRange?.toDate ?? null,
    });

    start = normalized.filterStart;
    end = normalized.filterEnd;
  } else if (
    config?.planStartDate &&
    selectedClaimTimePeriod !== "All" &&
    selectedClaimTimePeriod !== "Date of Service"
  ) {
    start = parseConfigDate(config.planStartDate, "start") ?? null;
    end = parseConfigDate(config?.planEndDate, "end") ?? null;
  }
  const applyDateFilter = Boolean(start && end);
  const applyMemberFilter = Boolean(selectedMembers && selectedMembers.length);
  const applyClaimTypeFilter =
    Boolean(selectedClaimType?.VALUE) && selectedClaimType.VALUE !== "All";
  const applyCareEntityFilter = Boolean(selectedCareEntity);
  const applyProcedureFilter =
    Boolean(selectedProcedure?.procedureCode) &&
    Boolean(selectedProcedure?.procedureDesc);
  const applyPharmacyFilter = Boolean(selectedPharmacy);
  const applyDrugNameFilter = Boolean(selectedDrugName);
  let filtered = groupedClaims
    .map((group) => {
      let historyList = [...group.historyList];
      // 1. Date Filter (compare by date only so same-day range includes claims)
      if (applyDateFilter) {
        const startDay = normalizeToISODate(start!);
        const endDay = normalizeToISODate(end!);
        historyList = historyList.filter((history) => {
          const svcDay = normalizeToISODate(history.recentSvcDate);
          return Boolean(svcDay) && svcDay >= startDay && svcDay <= endDay;
        });
      }
      // 2. Member Filter
      if (applyMemberFilter) {
        historyList = historyList.filter((history) =>
          selectedMembers.includes(history.memberId),
        );
      }
      // 3. Claim Type Filter
      if (applyClaimTypeFilter) {
        historyList = historyList.filter(
          (history) => history.claimType === selectedClaimType.VALUE,
        );
      }
      // 4. Care Entity Filter
      if (applyCareEntityFilter) {
        historyList = historyList.filter((history) => {
          const facility = selectedCareEntity;
          const type = selectedClaimType?.VALUE;
          if (type === "Medical") return history?.prvFullName === facility;
          if (type === "Pharmacy") return history?.prescriberName === facility;
          return (
            history?.prvFullName === facility ||
            history?.prescriberName === facility
          );
        });
      }
      // 5. Procedure Filter
      if (applyProcedureFilter) {
        historyList = historyList.filter((history) =>
          history.procedures?.some(
            (proc) =>
              proc.procedureCode === selectedProcedure.procedureCode &&
              proc.procedureDesc === selectedProcedure.procedureDesc,
          ),
        );
      }
      // 6. Pharmacy Filter
      if (applyPharmacyFilter) {
        const pharmacyLower = selectedPharmacy.toLowerCase();
        historyList = historyList.filter((history) => {
          const claimType = history.claimType;
          const prv = history?.prvFullName?.toLowerCase?.();
          if (selectedClaimType?.VALUE === "Pharmacy")
            return prv === pharmacyLower;
          if (selectedClaimType?.VALUE === "All")
            return claimType === "Pharmacy" && prv === pharmacyLower;
          return false;
        });
      }
      // 7. Drug Name Filter
      if (applyDrugNameFilter) {
        const drugLower = selectedDrugName.toLowerCase();
        historyList = historyList.filter((history) => {
          const claimType = history.claimType;
          const drug = history?.drugName?.toLowerCase?.();
          if (selectedClaimType?.VALUE === "Pharmacy")
            return drug === drugLower;
          if (selectedClaimType?.VALUE === "All")
            return claimType === "Pharmacy" && drug === drugLower;
          return false;
        });
      }
      return { ...group, historyList };
    })
    .filter((group) => group.historyList.length > 0);
  // 8. Sort (By Most Recent Date or Oldest First)
  if (sortClaimsBy.VALUE === "date-" || sortClaimsBy.VALUE === "date+") {
    const direction = sortClaimsBy.VALUE === "date-" ? -1 : 1;
    filtered = [...filtered]
      .sort((a, b) => {
        const aDate = new Date(a.historyList[0]?.recentSvcDate || "").getTime();
        const bDate = new Date(b.historyList[0]?.recentSvcDate || "").getTime();
        if (aDate === bDate) {
          const aId = parseInt(a.historyList[0]?.revClaimId || "0", 10);
          const bId = parseInt(b.historyList[0]?.revClaimId || "0", 10);
          return (aId - bId) * direction;
        }
        return (aDate - bDate) * direction;
      })
      .map((group) => ({
        ...group,
        historyList: [...group.historyList].sort((a, b) => {
          const aDate = new Date(a.recentSvcDate).getTime();
          const bDate = new Date(b.recentSvcDate).getTime();
          if (aDate === bDate) {
            const aId = parseInt(a.revClaimId || "0", 10);
            const bId = parseInt(b.revClaimId || "0", 10);
            return (bId - aId) * direction;
          }
          return (aDate - bDate) * direction;
        }),
      }));
  }
  return filtered;
}

export function getLegendItems(
  memberId: string,
  members: Record<string, Relation>,
  selectedMembers: string[],
  obj: CareHistoryGraphItemType,
): Payload[] {
  if (selectedMembers.length === 1 && selectedMembers[0] === memberId) {
    return [
      { value: "Family", type: "circle", color: obj?.colorFamily },
      {
        value: "Self",
        type: "circle",
        color: obj[`color${memberId}`] as string,
      },
    ];
  }
  return selectedMembers.map((id) => {
    const colorKey = `color${id}`;
    return {
      value: `${id === memberId ? "Self" : members[id].firstName}`,
      type: "circle",
      color: obj[colorKey] as string,
    };
  });
}

export function getDisabledStatus(
  dependent: Relation,
  authorizationAccessList: DataAccessListItem[],
  userMemberId: string,
  userRelationShip: string,
) {
  const isFromCareHistory = true;
  const dataAccessObj =
    authorizationAccessList && authorizationAccessList.length > 0
      ? hasDataAccess(dependent.memberId, authorizationAccessList, {
          isFromCareHistory,
        })
      : { hasAccess: false, status: null };
  if (dependent?.memberId === userMemberId) {
    return false;
  } else {
    if (dependent?.hipaaProtected) {
      if (
        dataAccessObj?.hasAccess &&
        dataAccessObj?.status !== DATA_ACCESS_ALL_STATUS.GRANTED
      ) {
        return false;
      } else {
        return true;
      }
    } else {
      return lowerCaseComparison(userRelationShip, RELATIONSHIP.DEPENDENT) ||
        lowerCaseComparison(userRelationShip, RELATIONSHIP.OTHERS)
        ? true
        : false;
    }
  }
}

export const filterUptoCurrentMonth = (
  data: CareHistoryGraphResponseType[],
) => {
  const now = Date.now();
  const d = new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "2-digit",
    timeZone: "UTC",
  })
    .formatToParts(now)
    .reduce(
      (acc, part) => {
        if (part.type === "year") acc.year = parseInt(part.value, 10);
        if (part.type === "month") acc.month = parseInt(part.value, 10);
        return acc;
      },
      {} as { year: number; month: number },
    );

  const currentYYYYMM = d.year * 100 + d.month;

  return data.filter((item) => item.serviceDate <= currentYYYYMM);
};

export function getClaimStatusBadgeColor(revAdjudicationCode: string) {
  switch (revAdjudicationCode) {
    case CLAIMS_STATUS.PAID:
      return "bg-claim-paid";
    case CLAIMS_STATUS.IN_PROCESS:
    case CLAIMS_STATUS.RECEIVED:
      return "bg-claim-processing";
    case CLAIMS_STATUS.REVERSED:
      return "bg-claim-reversed";
    case CLAIMS_STATUS.OTHERS:
      return "bg-claim-others";
    default:
      return "bg-claim-processing";
  }
}

export function getClaimStatusTooltipLabel(
  revAdjudicationCode: string,
  claimStatusUiLabels?: ClaimStatusLabel,
): string | undefined {
  switch (revAdjudicationCode) {
    case CLAIMS_STATUS.PAID:
      return claimStatusUiLabels?.paid || undefined;
    case CLAIMS_STATUS.IN_PROCESS:
      return claimStatusUiLabels?.in_process || undefined;
    case CLAIMS_STATUS.RECEIVED:
      return claimStatusUiLabels?.received || undefined;
    default:
      return undefined;
  }
}
